import { useMemo, useState } from 'react'
import { usePage } from '@inertiajs/react'
import { Checkbox } from '@/shared/ui/checkbox'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { formatSiteDateTime, toWallClockString } from '@/shared/lib/tenantDateTime'
import { t } from '@/shared/lib/i18n'
import {
  humanReadableStructuralRrule,
  parseRruleParams,
  splitRruleTime,
} from '@/shared/lib/frequency/rrule-utils'
import {
  computeNextTriggerPreview,
  computeOverduePreviewDate,
} from '@/shared/lib/frequency/overdue-preview'

export const RRULE_FREQ_OPTIONS = [
  { value: 'MINUTELY', label: () => t('instruments.tasksSchedule.form.rrule.freqMinutely') },
  { value: 'HOURLY', label: () => t('instruments.tasksSchedule.form.rrule.freqHourly') },
  { value: 'DAILY', label: () => t('instruments.tasksSchedule.form.rrule.freqDaily') },
  { value: 'WEEKLY', label: () => t('instruments.tasksSchedule.form.rrule.freqWeekly') },
  { value: 'MONTHLY', label: () => t('instruments.tasksSchedule.form.rrule.freqMonthly') },
  { value: 'YEARLY', label: () => t('instruments.tasksSchedule.form.rrule.freqYearly') },
]

export const WEEKDAY_OPTIONS = [
  { byday: 'MO', label: () => t('instruments.tasksSchedule.form.rrule.dayMon') },
  { byday: 'TU', label: () => t('instruments.tasksSchedule.form.rrule.dayTue') },
  { byday: 'WE', label: () => t('instruments.tasksSchedule.form.rrule.dayWed') },
  { byday: 'TH', label: () => t('instruments.tasksSchedule.form.rrule.dayThu') },
  { byday: 'FR', label: () => t('instruments.tasksSchedule.form.rrule.dayFri') },
  { byday: 'SA', label: () => t('instruments.tasksSchedule.form.rrule.daySat') },
  { byday: 'SU', label: () => t('instruments.tasksSchedule.form.rrule.daySun') },
]

export const MONTHLY_POSITION_OPTIONS = [
  { value: 1, label: () => t('instruments.tasksSchedule.form.rrule.monthlyPositionFirst') },
  { value: 2, label: () => t('instruments.tasksSchedule.form.rrule.monthlyPositionSecond') },
  { value: 3, label: () => t('instruments.tasksSchedule.form.rrule.monthlyPositionThird') },
  { value: 4, label: () => t('instruments.tasksSchedule.form.rrule.monthlyPositionFourth') },
  { value: -1, label: () => t('instruments.tasksSchedule.form.rrule.monthlyPositionLast') },
]

// Conservative max days per month. February uses 29 to allow leap-year scheduling
// (BYMONTHDAY=29+BYMONTH=2 fires only on Feb 29 in leap years). Values 30 and 31 for
// February are prevented because they never naturally occur even in leap years.
const DAYS_IN_MONTH: Record<number, number> = {
  1: 31,
  2: 29,
  3: 31,
  4: 30,
  5: 31,
  6: 30,
  7: 31,
  8: 31,
  9: 30,
  10: 31,
  11: 30,
  12: 31,
}

export const MONTH_OPTIONS = [
  { value: 1, label: () => t('instruments.tasksSchedule.form.rrule.yearlyMonthJanuary') },
  { value: 2, label: () => t('instruments.tasksSchedule.form.rrule.yearlyMonthFebruary') },
  { value: 3, label: () => t('instruments.tasksSchedule.form.rrule.yearlyMonthMarch') },
  { value: 4, label: () => t('instruments.tasksSchedule.form.rrule.yearlyMonthApril') },
  { value: 5, label: () => t('instruments.tasksSchedule.form.rrule.yearlyMonthMay') },
  { value: 6, label: () => t('instruments.tasksSchedule.form.rrule.yearlyMonthJune') },
  { value: 7, label: () => t('instruments.tasksSchedule.form.rrule.yearlyMonthJuly') },
  { value: 8, label: () => t('instruments.tasksSchedule.form.rrule.yearlyMonthAugust') },
  { value: 9, label: () => t('instruments.tasksSchedule.form.rrule.yearlyMonthSeptember') },
  { value: 10, label: () => t('instruments.tasksSchedule.form.rrule.yearlyMonthOctober') },
  { value: 11, label: () => t('instruments.tasksSchedule.form.rrule.yearlyMonthNovember') },
  { value: 12, label: () => t('instruments.tasksSchedule.form.rrule.yearlyMonthDecember') },
]

export interface RruleParams {
  freq: string
  interval: number
  weeklyMode: 'none' | 'days'
  selectedDays: string[]
  // 'none' = plain FREQ=MONTHLY;INTERVAL=n, anchor from first execution (legacy parity)
  monthlyMode: 'none' | 'specific_day' | 'relative'
  monthlyDay: number
  monthlyPosition: number
  monthlyWeekday: string
  yearlyMode: 'none' | 'specific'
  yearlyDay: number
  yearlyMonth: number
}

export const buildRruleString = (params: RruleParams): string => {
  const {
    freq,
    interval,
    weeklyMode,
    selectedDays,
    monthlyMode,
    monthlyDay,
    monthlyPosition,
    monthlyWeekday,
    yearlyMode,
    yearlyDay,
    yearlyMonth,
  } = params
  let rrule = `FREQ=${freq};INTERVAL=${interval}`
  if (freq === 'WEEKLY' && weeklyMode === 'days' && selectedDays.length > 0) {
    rrule += `;BYDAY=${selectedDays.join(',')}`
  }
  if (freq === 'MONTHLY') {
    if (monthlyMode === 'specific_day') {
      rrule += `;BYMONTHDAY=${monthlyDay}`
    } else if (monthlyMode === 'relative') {
      rrule += `;BYSETPOS=${monthlyPosition};BYDAY=${monthlyWeekday}`
    }
    // 'none' emits plain FREQ=MONTHLY;INTERVAL=n — anchor from first execution
  }
  if (freq === 'YEARLY' && yearlyMode === 'specific') {
    rrule += `;BYMONTHDAY=${yearlyDay};BYMONTH=${yearlyMonth}`
  }
  return rrule
}

export function humanReadableRrule(rruleString: string): string {
  return humanReadableStructuralRrule(rruleString)
}

interface RruleBuilderProps {
  value: string
  onChange: (rrule: string) => void
  lateTimeMinutes?: number
  startsAt?: string
  endsAtMidnight?: boolean
  idPrefix?: string
}

// Build initial state from a stored RRULE (structural — no BYHOUR/BYMINUTE).
// Uses RRule.fromString().origOptions for AC2 hydration parity with legacy.
const initFromRrule = (rrule: string): RruleParams => {
  const defaults: RruleParams = {
    freq: 'WEEKLY',
    interval: 1,
    weeklyMode: 'none',
    selectedDays: [],
    monthlyMode: 'specific_day',
    monthlyDay: 1,
    monthlyPosition: 1,
    monthlyWeekday: 'MO',
    yearlyMode: 'none',
    yearlyDay: 1,
    yearlyMonth: 1,
  }
  const parsed = parseRruleParams(rrule)
  return { ...defaults, ...parsed }
}

export const RruleBuilder = ({
  value,
  onChange,
  lateTimeMinutes,
  startsAt,
  endsAtMidnight,
  idPrefix = 'rrule',
}: RruleBuilderProps) => {
  const { tenantTimeFormat, tenantTimeZone } = usePage<{
    tenantTimeFormat?: number | null
    tenantTimeZone?: string | null
  }>().props

  // Strip time components before initialising builder state — callers own the time.
  const { structuralRrule: initialStructural } = splitRruleTime(value)

  const [freq, setFreq] = useState<string>(() => initFromRrule(initialStructural).freq)
  const [interval, setInterval] = useState<number>(() => initFromRrule(initialStructural).interval)
  const [weeklyMode, setWeeklyMode] = useState<'none' | 'days'>(
    () => initFromRrule(initialStructural).weeklyMode,
  )
  const [selectedDays, setSelectedDays] = useState<string[]>(
    () => initFromRrule(initialStructural).selectedDays,
  )
  const [monthlyMode, setMonthlyMode] = useState<'none' | 'specific_day' | 'relative'>(
    () => initFromRrule(initialStructural).monthlyMode,
  )
  const [monthlyDay, setMonthlyDay] = useState<number>(
    () => initFromRrule(initialStructural).monthlyDay,
  )
  const [monthlyPosition, setMonthlyPosition] = useState<number>(
    () => initFromRrule(initialStructural).monthlyPosition,
  )
  const [monthlyWeekday, setMonthlyWeekday] = useState<string>(
    () => initFromRrule(initialStructural).monthlyWeekday,
  )
  const [yearlyMode, setYearlyMode] = useState<'none' | 'specific'>(
    () => initFromRrule(initialStructural).yearlyMode,
  )
  const [yearlyDay, setYearlyDay] = useState<number>(
    () => initFromRrule(initialStructural).yearlyDay,
  )
  const [yearlyMonth, setYearlyMonth] = useState<number>(
    () => initFromRrule(initialStructural).yearlyMonth,
  )
  const computedRrule = useMemo(
    () =>
      buildRruleString({
        freq,
        interval,
        weeklyMode,
        selectedDays,
        monthlyMode,
        monthlyDay,
        monthlyPosition,
        monthlyWeekday,
        yearlyMode,
        yearlyDay,
        yearlyMonth,
      }),
    [
      freq,
      interval,
      weeklyMode,
      selectedDays,
      monthlyMode,
      monthlyDay,
      monthlyPosition,
      monthlyWeekday,
      yearlyMode,
      yearlyDay,
      yearlyMonth,
    ],
  )

  const preview = useMemo(() => humanReadableStructuralRrule(computedRrule), [computedRrule])

  const resolvedTenantTimeZone = tenantTimeZone ?? 'UTC'

  // "If the task is completed now" preview (legacy frequencies-list.component.ts
  // #createRRuleFrequency previewDate), computed in tenant wall-clock time so neither the
  // browser zone (REBUILD-592) nor DST (REBUILD-263) shifts Starts At.
  // REBUILD-369: explicit empty Starts At (`''`) suppresses next-execution preview.
  const nextExecution = useMemo(
    () =>
      computeNextTriggerPreview({
        structuralRrule: computedRrule,
        startsAt,
        tenantTimeZone: resolvedTenantTimeZone,
      }),
    [computedRrule, startsAt, resolvedTenantTimeZone],
  )

  const overdueDate = useMemo(() => {
    if (!nextExecution || !lateTimeMinutes) return null
    // REBUILD-592 / Frequency Test Matrix: full window minus 1s; checked clamps to 23:59:59
    // on the day before the raw landing (not the trigger day when overdue is multi-day).
    return computeOverduePreviewDate({
      nextExecution,
      lateTimeMinutes,
      endsAtMidnight,
      tenantTimeZone: resolvedTenantTimeZone,
    })
  }, [nextExecution, lateTimeMinutes, endsAtMidnight, resolvedTenantTimeZone])

  const buildAndEmit = (overrides: Partial<RruleParams>) => {
    const params: RruleParams = {
      freq,
      interval,
      weeklyMode,
      selectedDays,
      monthlyMode,
      monthlyDay,
      monthlyPosition,
      monthlyWeekday,
      yearlyMode,
      yearlyDay,
      yearlyMonth,
      ...overrides,
    }
    onChange(buildRruleString(params))
  }

  const handleFreqChange = (newFreq: string) => {
    setFreq(newFreq)
    buildAndEmit({ freq: newFreq })
  }

  const handleIntervalChange = (newInterval: number) => {
    const safeInterval = Math.max(1, Number.isNaN(newInterval) ? 1 : newInterval)
    setInterval(safeInterval)
    buildAndEmit({ interval: safeInterval })
  }

  const handleWeeklyModeChange = (mode: 'none' | 'days') => {
    setWeeklyMode(mode)
    buildAndEmit({ weeklyMode: mode })
  }

  const toggleDay = (byday: string) => {
    const updated = selectedDays.includes(byday)
      ? selectedDays.filter((day) => day !== byday)
      : [...selectedDays, byday]
    setSelectedDays(updated)
    buildAndEmit({ selectedDays: updated })
  }

  const handleMonthlyModeChange = (mode: 'none' | 'specific_day' | 'relative') => {
    setMonthlyMode(mode)
    buildAndEmit({ monthlyMode: mode })
  }

  const handleMonthlyDayChange = (day: number) => {
    const safeDay = Math.min(31, Math.max(1, Number.isNaN(day) ? 1 : day))
    setMonthlyDay(safeDay)
    buildAndEmit({ monthlyDay: safeDay })
  }

  const handleMonthlyPositionChange = (position: number) => {
    setMonthlyPosition(position)
    buildAndEmit({ monthlyPosition: position })
  }

  const handleMonthlyWeekdayChange = (weekday: string) => {
    setMonthlyWeekday(weekday)
    buildAndEmit({ monthlyWeekday: weekday })
  }

  const handleYearlyModeChange = (mode: 'none' | 'specific') => {
    setYearlyMode(mode)
    buildAndEmit({ yearlyMode: mode })
  }

  const handleYearlyDayChange = (day: number) => {
    const maxDay = DAYS_IN_MONTH[yearlyMonth] ?? 31
    const safeDay = Math.min(maxDay, Math.max(1, Number.isNaN(day) ? 1 : day))
    setYearlyDay(safeDay)
    buildAndEmit({ yearlyDay: safeDay })
  }

  const handleYearlyMonthChange = (month: number) => {
    const maxDay = DAYS_IN_MONTH[month] ?? 31
    const clampedDay = Math.min(yearlyDay, maxDay)
    setYearlyMonth(month)
    setYearlyDay(clampedDay)
    buildAndEmit({ yearlyMonth: month, yearlyDay: clampedDay })
  }

  return (
    <div className="space-y-3 rounded-md border p-3 bg-muted/30">
      <span className="text-sm font-medium">
        {t('instruments.tasksSchedule.form.rrule.sectionTitle')}
      </span>

      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">
              {t('instruments.tasksSchedule.form.rrule.builderFrequency')}
            </Label>
            <Select value={freq} onValueChange={handleFreqChange}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RRULE_FREQ_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">
              {t('instruments.tasksSchedule.form.rrule.builderInterval')}
            </Label>
            <Input
              type="number"
              min={1}
              value={interval}
              onChange={(event) => handleIntervalChange(parseInt(event.target.value, 10))}
              className="h-8 text-sm"
            />
          </div>
        </div>

        {freq === 'WEEKLY' && (
          <div className="space-y-2">
            <Label className="text-xs">
              {t('instruments.tasksSchedule.form.rrule.builderDays')}
            </Label>
            <RadioGroup
              value={weeklyMode}
              onValueChange={(v) => handleWeeklyModeChange(v as 'none' | 'days')}
              className="flex flex-col gap-1"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="none" id={`${idPrefix}-weekly-mode-none`} />
                <Label
                  htmlFor={`${idPrefix}-weekly-mode-none`}
                  className="text-xs font-normal cursor-pointer"
                >
                  {t('instruments.tasksSchedule.form.rrule.weeklyModeNone')}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="days" id={`${idPrefix}-weekly-mode-days`} />
                <Label
                  htmlFor={`${idPrefix}-weekly-mode-days`}
                  className="text-xs font-normal cursor-pointer"
                >
                  {t('instruments.tasksSchedule.form.rrule.weeklyModeSelectDays')}
                </Label>
              </div>
            </RadioGroup>
            {weeklyMode === 'days' && (
              <div className="flex flex-wrap gap-2 pt-1">
                {WEEKDAY_OPTIONS.map((day) => (
                  <div key={day.byday} className="flex items-center gap-1">
                    <Checkbox
                      id={`${idPrefix}-day-${day.byday}`}
                      checked={selectedDays.includes(day.byday)}
                      onCheckedChange={() => toggleDay(day.byday)}
                    />
                    <Label
                      htmlFor={`${idPrefix}-day-${day.byday}`}
                      className="text-xs font-normal cursor-pointer"
                    >
                      {day.label()}
                    </Label>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {freq === 'MONTHLY' && (
          <div className="space-y-2">
            <Label className="text-xs">
              {t('instruments.tasksSchedule.form.rrule.monthlyModeLabel')}
            </Label>
            <RadioGroup
              value={monthlyMode}
              onValueChange={(v) =>
                handleMonthlyModeChange(v as 'none' | 'specific_day' | 'relative')
              }
              className="flex flex-col gap-1"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="none" id={`${idPrefix}-monthly-mode-none`} />
                <Label
                  htmlFor={`${idPrefix}-monthly-mode-none`}
                  className="text-xs font-normal cursor-pointer"
                >
                  {t('instruments.tasksSchedule.form.rrule.monthlyModeNone')}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="specific_day" id={`${idPrefix}-monthly-mode-specific`} />
                <Label
                  htmlFor={`${idPrefix}-monthly-mode-specific`}
                  className="text-xs font-normal cursor-pointer"
                >
                  {t('instruments.tasksSchedule.form.rrule.monthlyModeSpecificDay')}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="relative" id={`${idPrefix}-monthly-mode-relative`} />
                <Label
                  htmlFor={`${idPrefix}-monthly-mode-relative`}
                  className="text-xs font-normal cursor-pointer"
                >
                  {t('instruments.tasksSchedule.form.rrule.monthlyModeRelative')}
                </Label>
              </div>
            </RadioGroup>
            {monthlyMode === 'specific_day' && (
              <div className="space-y-1">
                <Label className="text-xs">
                  {t('instruments.tasksSchedule.form.rrule.monthlyDayLabel')}
                </Label>
                <Input
                  type="number"
                  min={1}
                  max={31}
                  value={monthlyDay}
                  onChange={(event) => handleMonthlyDayChange(parseInt(event.target.value, 10))}
                  className="h-8 text-sm w-24"
                />
                {monthlyDay > 28 && (
                  <p className="text-xs text-muted-foreground">
                    {t('instruments.tasksSchedule.form.rrule.monthlyDayShortMonthHint')}
                  </p>
                )}
              </div>
            )}
            {monthlyMode === 'relative' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">
                    {t('instruments.tasksSchedule.form.rrule.monthlyPositionLabel')}
                  </Label>
                  <Select
                    value={String(monthlyPosition)}
                    onValueChange={(v) => handleMonthlyPositionChange(parseInt(v, 10))}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHLY_POSITION_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={String(option.value)}>
                          {option.label()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">
                    {t('instruments.tasksSchedule.form.rrule.monthlyWeekdayLabel')}
                  </Label>
                  <Select value={monthlyWeekday} onValueChange={handleMonthlyWeekdayChange}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WEEKDAY_OPTIONS.map((day) => (
                        <SelectItem key={day.byday} value={day.byday}>
                          {day.label()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        )}

        {freq === 'YEARLY' && (
          <div className="space-y-2">
            <Label className="text-xs">
              {t('instruments.tasksSchedule.form.rrule.yearlyModeLabel')}
            </Label>
            <RadioGroup
              value={yearlyMode}
              onValueChange={(v) => handleYearlyModeChange(v as 'none' | 'specific')}
              className="flex flex-col gap-1"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="none" id={`${idPrefix}-yearly-mode-none`} />
                <Label
                  htmlFor={`${idPrefix}-yearly-mode-none`}
                  className="text-xs font-normal cursor-pointer"
                >
                  {t('instruments.tasksSchedule.form.rrule.yearlyModeNone')}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="specific" id={`${idPrefix}-yearly-mode-specific`} />
                <Label
                  htmlFor={`${idPrefix}-yearly-mode-specific`}
                  className="text-xs font-normal cursor-pointer"
                >
                  {t('instruments.tasksSchedule.form.rrule.yearlyModeSpecificDay')}
                </Label>
              </div>
            </RadioGroup>
            {yearlyMode === 'specific' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">
                    {t('instruments.tasksSchedule.form.rrule.yearlyDayLabel')}
                  </Label>
                  <Input
                    type="number"
                    min={1}
                    max={DAYS_IN_MONTH[yearlyMonth] ?? 31}
                    value={yearlyDay}
                    onChange={(event) => handleYearlyDayChange(parseInt(event.target.value, 10))}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">
                    {t('instruments.tasksSchedule.form.rrule.yearlyMonthLabel')}
                  </Label>
                  <Select
                    value={String(yearlyMonth)}
                    onValueChange={(v) => handleYearlyMonthChange(parseInt(v, 10))}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTH_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={String(option.value)}>
                          {option.label()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {preview && (
        <p className="text-xs text-muted-foreground">
          <span className="font-medium">
            {t('instruments.tasksSchedule.form.rrule.builderPreview')}:{' '}
          </span>
          {preview}
        </p>
      )}

      {nextExecution && (
        <p className="text-xs text-muted-foreground">
          <span className="font-medium">
            {t('instruments.tasksSchedule.form.rrule.nextTriggerLabel')}:{' '}
          </span>
          {formatSiteDateTime(
            toWallClockString(nextExecution, tenantTimeZone ?? 'UTC'),
            tenantTimeZone ?? 'UTC',
            tenantTimeFormat,
          )}
        </p>
      )}

      {overdueDate && (
        <p className="text-xs text-muted-foreground">
          <span className="font-medium">
            {t('instruments.tasksSchedule.form.rrule.overdueDateLabel')}:{' '}
          </span>
          {formatSiteDateTime(
            toWallClockString(overdueDate, tenantTimeZone ?? 'UTC'),
            tenantTimeZone ?? 'UTC',
            tenantTimeFormat,
          )}
        </p>
      )}
    </div>
  )
}
